<template>
    <div 
        id="hero-banner"
        class="text-navy-dark"
        :class="background && background.color"
    >
        <div
            :class="
                background && background.style && background.style.length
                    ? background.style
                    : 'bg-repeat-x bg-bottom'
            "
            :style="backgroundStyle"
        >
            <div
                :class="flexDirection"
                class="widescreen:container flex justify-between flex-col-reverse"
            >
                <!-- left side -->
                <div
                    class="max-w-full desktop:w-1/2 my-auto px-6 py-6 tablet:py-12 tablet:px-12 widescreen:py-10 widescreen:px-20 z-10"
                >
                    <!--<h2>LEFT</h2>-->

                    <h3
                        v-if="eyebrow && eyebrow.text"
                        v-html="eyebrow.text"
                        class="text-lg desktop:text-xl inline-block mb-3 rounded"
                        :class="
                            eyebrow.style && eyebrow.style.length > 0
                                ? eyebrow.style
                                : 'px-0 py-0 bg-transparent text-blue'
                        "
                    />

                    <h4
                        v-if="preHeading && preHeading.text"
                        v-html="preHeading.text"
                        class="text-xl desktop:text-2xl, font-bold, mb-3"
                        :class="preHeading.style"
                    />

                    <h1
                        v-if="heading && heading.text"
                        v-html="heading.text"
                        class="desktop:leading-15 text-3xl tablet:text-4xl desktop:text-5xl mb-3"
                        :class="heading.style"
                    />

                    <h4
                        v-if="subHeading && subHeading.text"
                        v-html="subHeading.text"
                        class="text-lg tablet:text-xl desktop:text-2xl font-bold mb-3"
                        :class="subHeading.style"
                    />

                    <p
                        v-if="subContent && subContent.text"
                        v-html="subContent.text"
                        class="mb-3"
                        :class="subContent.style"
                    />

                    <hr
                        v-if="
                            headingDividerColor &&
                            headingDividerColor !== 'none'
                        "
                        class="my-5"
                        :class="headingDividerColor"
                    />

                    <h5
                        v-if="priceHeading && priceHeading.text"
                        v-html="priceHeading.text"
                        class="text-lg desktop:text-xl mb-1.5"
                        :class="priceHeading.style"
                    />

                    <div
                        v-if="price && price.length"
                        class="flex flex-col desktop:flex-row gap-x-6 items-start desktop:items-center mb-3"
                    >
                        <template v-for="(p, index) in price">
                            <div
                                v-if="index > 0"
                                class="text-5xl ml-20 tablet:ml-24 desktop:ml-0 desktop:self-center font-heavy"
                                :class="p.color"
                            >
                                &plus;
                            </div>
                            <div
                                :key="index"
                                :class="p.color"
                            >
                                <div
                                    class="flex leading-none font-heavy items-center justify-end"
                                >
                                    <span class="text-5xl self-start">$</span>
                                    <span class="mr-2 text-8xl">{{
                                        splitPrice(p.value)[0]
                                    }}</span>
                                    <div>
                                        <span class="text-3xl block">{{
                                            splitPrice(p.value)[1]
                                        }}</span>
                                        <span class="text-3xl block">{{
                                            p.suffix
                                        }}</span>
                                    </div>
                                </div>
                                <!-- <h5 class="text-xl" :class="(p.leftAligned? 'text-left ': 'text-right ') + p.color" v-html="p.description" /> -->
                                <h5
                                    class="text-xl text-right"
                                    :class="p.color"
                                    v-html="p.description"
                                />
                            </div>
                        </template>
                    </div>

                    <div
                        v-if="priceFooter && priceFooter.text"
                        v-html="priceFooter.text"
                        class="text-lg desktop:text-xl font-bold mb-3"
                        :class="priceFooter.style"
                    />

                    <div
                        v-if="priceSubFooter && priceSubFooter.text"
                        v-html="priceSubFooter.text"
                        :class="
                            priceSubFooter.style +
                            (priceDividerColor !== 'none' ? ' mb-3' : '')
                        "
                    />

                    <hr
                        v-if="priceDividerColor && priceDividerColor !== 'none'"
                        class="my-5"
                        :class="priceDividerColor"
                    />

                    <div
                        v-if="cta && cta.length"
                        class="flex flex-col items-start tablet:items-center tablet:flex-row gap-x-6 desktop:items-center"
                    >
                        <template v-for="c in cta">
                            <button
                                v-if="c.url.includes(SUB.CHECK_AVAILABILITY)"
                                class="track mb-2"
                                :key="c.text + 1"
                                v-bind="ctaProps(c)"
                                @click="toggleCheckAvailability()"
                            >
                                {{ c.text }}
                            </button>
                            <a
                                v-else
                                class="track mb-2 mt-2 tablet:mt-4 w-full tablet:w-auto text-center"
                                :key="c.text"
                                v-bind="ctaProps(c)"
                            >
                                {{ c.text }}
                            </a>
                        </template>
                    </div>

                    <HeroSpeedQuiz
                        v-if="showCalculator"
                        class=""
                        theme="themeAlt"
                        :product-metadata="productMetadata"
                        :promo-link="promoLink"
                        :experience="experience"
                    />

                    <div
                        v-if="disclaimer"
                        v-html="disclaimer"
                    />
                </div>

                <!-- right side -->
                <div
                    class="desktop:w-1/2 z-0 relative max-h-full flex items-center justify-center"
                >
                    <img
                        v-if="image"
                        fetchpriority="high"
                        :src="image.url"
                        :alt="image.altTag"
                        class="w-full h-full"
                        :class="
                            image.style && image.style.length > 0
                                ? image.style
                                : 'object-cover'
                        "
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// Use this component directly in Craft (via a Twig file), or with Personalization (wrapped in personalization.vue)
// Props are strictly validated! Keep an eye on the console for errors.

import validate from './validators'

const COLORS = [
    '',
    'bg-white',
    'bg-salmon',
    'bg-blue-lightest',
    'bg-blue-light',
    'bg-blue',
    'bg-storm',
    'bg-teal',
    'bg-navy-light',
    'bg-navy',
    'bg-navy-dark',
    'bg-yellow',
    'bg-gradient-salmon',
    'bg-gradient-navy-dark-salmon',
    'bg-gradient-blue-light',
    'bg-gradient-blue',
    'bg-gradient-teal',
    'bg-gray-100',
    'bg-gray-200',
    'bg-gray-300',
    'bg-gray-400',
    'bg-gray-500',
]

const DIVIDER_COLORS = [
    '',
    'none',
    'border-white',
    'border-salmon',
    'border-blue-lightest',
    'border-blue-light',
    'border-blue',
    'border-storm',
    'border-teal',
    'border-navy-light',
    'border-navy',
    'border-navy-dark',
    'border-yellow',
    'border-gray-100',
    'border-gray-200',
    'border-gray-300',
    'border-gray-400',
    'border-gray-500',
]

const TEXT_COLORS = [
    '',
    'text-white',
    'text-salmon',
    'text-blue',
    'text-storm',
    'text-navy',
    'text-navy-dark',
    'text-gray-400',
    'text-gray-500',
]

const FORMATTING_STYLES = [
    'm-0',
    'm-1',
    'm-2',
    'm-3',
    'm-4',
    'm-5',
    'm-6',
    'mt-0',
    'mt-1',
    'mt-2',
    'mt-3',
    'mt-4',
    'mt-5',
    'mt-6',
    'mb-0',
    'mb-1',
    'mb-2',
    'mb-3',
    'mb-4',
    'mb-5',
    'mb-6',
    'mr-0',
    'mr-1',
    'mr-2',
    'mr-3',
    'mr-4',
    'mr-5',
    'mr-6',
    'ml-0',
    'ml-1',
    'ml-2',
    'ml-3',
    'ml-4',
    'ml-5',
    'ml-6',
    'p-0',
    'p-1',
    'p-2',
    'p-3',
    'p-4',
    'p-5',
    'p-6',
    'pt-0',
    'pt-1',
    'pt-2',
    'pt-3',
    'pt-4',
    'pt-5',
    'pt-6',
    'pb-0',
    'pb-1',
    'pb-2',
    'pb-3',
    'pb-4',
    'pb-5',
    'pb-6',
    'pr-0',
    'pr-1',
    'pr-2',
    'pr-3',
    'pr-4',
    'pr-5',
    'pr-6',
    'pl-0',
    'pl-1',
    'pl-2',
    'pl-3',
    'pl-4',
    'pl-5',
    'pl-6',
    'h-full',
    'h-auto',
    'w-full',
    'w-auto',
]

const IMAGE_STYLES = [
    ...FORMATTING_STYLES,
    '',
    'object-contain',
    'object-cover',
    'object-fill',
    'object-none',
    'object-scale-down',
    'object-bottom',
    'object-center',
    'object-left',
    'object-left-bottom',
    'object-left-top',
    'object-right',
    'object-right-bottom',
    'object-right-top',
    'object-top',
    'bg-repeat',
    'bg-no-repeat',
    'bg-repeat-x',
    'bg-repeat-y',
    'bg-repeat-round',
    'bg-repeat-space',
    'bg-auto',
    'bg-cover',
    'bg-contain',
    'bg-bottom',
    'bg-center',
    'bg-left',
    'bg-left-bottom',
    'bg-left-top',
    'bg-right',
    'bg-right-bottom',
    'bg-right-top',
    'bg-top',
]

const TEXT_STYLES = [
    ...COLORS,
    ...TEXT_COLORS,
    ...FORMATTING_STYLES,
    'uppercase',
    'lowercase',
    'capitalize',
    'normal-case',
    'font-book',
    'font-normal',
    'font-bold',
    'font-heavy',
    'italic',
    'text-xs',
    'text-sm',
    'text-md',
    'text-base',
    'text-lg',
    'text-xl',
    'text-2xl',
    'text-3xl',
    'text-4xl',
    'text-5xl',
    'text-6xl',
    'text-7xl',
    'underline',
    'overline',
    'line-through',
    'no-underline',
]

const TEXT_STYLE = {
    text: ['isNullable', 'isValidString'],
    style: [
        // 'isDefined',
        'isArrayOfValidStrings',
        (style) => style.every((s) => TEXT_STYLES.includes(s)),
    ],
}

const SUB = {
    GO_TO_CART: '<go-to-cart>',
    PRODUCT_NAME: '<product-name>',
    CHECK_AVAILABILITY: '<check-availability>',
}

import HeroSpeedQuiz from './speedQuiz.vue'

export default {
    name: 'HeroBanner',
    components: {
        HeroSpeedQuiz,
    },
    props: {
        productMetadata: {
            type: String,
            required: false,
            default: '[]',
        },
        contentZone: {
            type: String,
            required: false,
            default: null,
            validator: (val) =>
                validate(
                    { val },
                    {
                        val: ['isDefined', 'isValidString'],
                    }
                ),
        },
        experience: {
            type: String,
            required: false,
            default: null,
            validator: (val) =>
                validate(
                    { val },
                    {
                        val: ['isDefined', 'isValidString'],
                    }
                ),
        },
        background: {
            type: Object,
            required: false,
            default: () => null,
            validator: (val) =>
                validate(val, {
                    color: ['isDefined', (color) => COLORS.includes(color)],
                    image: ['isNullable', 'isValidOrBlankUrl'],
                    style: [
                        //'isDefined',
                        'isArrayOfValidStrings',
                        (style) => style.every((s) => IMAGE_STYLES.includes(s)),
                    ],
                }),
        },
        layoutReverse: {
            type: Boolean,
            required: false,
            default: false,
        },
        image: {
            type: Object,
            required: false,
            default: () => null,
            validator: (val) =>
                validate(val, {
                    url: ['isDefined', 'isValidOrBlankUrl'],
                    altTag: ['isDefined', 'isValidString'],
                    style: [
                        //'isDefined',
                        'isArrayOfValidStrings',
                        (style) => style.every((s) => IMAGE_STYLES.includes(s)),
                    ],
                }),
        },
        eyebrow: {
            type: Object,
            required: false,
            default: () => null,
            validator: (val) => validate(val, TEXT_STYLE),
        },
        preHeading: {
            type: Object,
            required: false,
            default: () => null,
            validator: (val) => validate(val, TEXT_STYLE),
        },
        heading: {
            type: Object,
            required: false,
            default: () => null,
            validator: (val) => validate(val, TEXT_STYLE),
        },
        subHeading: {
            type: Object,
            required: false,
            default: () => null,
            validator: (val) => validate(val, TEXT_STYLE),
        },
        subContent: {
            type: Object,
            required: false,
            default: () => null,
            validator: (val) => validate(val, TEXT_STYLE),
        },
        headingDividerColor: {
            type: String,
            required: false,
            default: 'none',
            validator: (val) => DIVIDER_COLORS.includes(val),
        },
        cta: {
            type: Array,
            required: false,
            default: () => [],
            validator: (val) =>
                val.every((v) =>
                    validate(v, {
                        type: [
                            'isDefined',
                            (type) =>
                                [
                                    'button-primary',
                                    'button-secondary',
                                    'button-salmon',
                                    'link',
                                ].includes(type),
                        ],
                        url: [
                            'isDefined',
                            (url) => {
                                if (
                                    url === SUB.GO_TO_CART ||
                                    url === SUB.CHECK_AVAILABILITY
                                ) {
                                    return true
                                } else {
                                    return validate({ url }, ['isValidUrl'])
                                }
                            },
                        ],
                        text: ['isDefined', 'isValidString'],
                        gaCategory: ['isValidString'],
                        gaAction: ['isValidString'],
                        gaLabel: ['isValidString'],
                    })
                ),
        },
        priceHeading: {
            type: Object,
            required: false,
            default: () => null,
            validator: (val) => validate(val, TEXT_STYLE),
        },
        price: {
            type: Array,
            required: false,
            default: () => [],
            validator: (val) =>
                val.every((v) =>
                    validate(v, {
                        description: ['isNullable'],
                        value: ['isNullable', 'isValidPrice'],
                        color: [
                            'isNullable',
                            (color) => TEXT_COLORS.includes(color),
                        ],
                        leftAligned: ['isNullable', 'isBoolean'],
                    })
                ),
        },
        priceFooter: {
            type: Object,
            required: false,
            default: () => null,
            validator: (val) => validate(val, TEXT_STYLE),
        },
        priceSubFooter: {
            type: Object,
            required: false,
            default: () => null,
            validator: (val) => validate(val, TEXT_STYLE),
        },
        priceDividerColor: {
            type: String,
            required: false,
            default: 'none',
            validator: (val) => DIVIDER_COLORS.includes(val),
        },
        disclaimer: {
            type: String,
            required: false,
            default: null,
            validator: (val) =>
                validate(
                    { val },
                    {
                        val: ['isDefined', 'isValidString'],
                    }
                ),
        },
        showCalculator: {
            type: Boolean,
            required: false,
            default: false,
        },
        showCountdown: {
            type: Boolean,
            required: false,
            default: false,
        },
        countdown: {
            type: Object,
            required: false,
            default: () => null,
            validator: (val) =>
                validate(val, {
                    text: ['isDefined', 'isValidString'],
                    style: [
                        'isDefined',
                        'isArrayOfValidStrings',
                        (style) => style.every((s) => TEXT_STYLES.includes(s)),
                    ],
                    endDate: ['isDefined'],
                    fallback: ['isDefined', 'isValidString'],
                }),
        },
        promoLink: {
            type: String,
            default: null,
        },
        identifier: {
            type: String,
            default: '',
        },
    },
    data: () => ({
        showingCountdownLayer: true,
        SUB: SUB,
    }),
    mounted() {
        if (document.getElementById('heroBannerSkeleton' + this.identifier)) {
            document.getElementById('heroBannerSkeleton' + this.identifier).remove()
        }
    },
    computed: {
        backgroundStyle() {
            return {
                backgroundImage: this.background?.image
                    ? `url(${this.background?.image})`
                    : 'none',
            }
        },
        flexDirection() {
            return this.layoutReverse
                ? 'desktop:flex-row-reverse'
                : 'desktop:flex-row'
        },
    },
    methods: {
        toggleCheckAvailability() {
            if (this.$store.getters.showOfferCards) {
                window.location.href = `${window.location.pathname}#offers`
            } else {
                this.$store.dispatch('toggleCheckAvailabilityModal', {
                    visible: true,
                    currentCustomerCheck: false,
                    movingToAddress: false,
                })
            }
        },
        productify(val) {
            if (!val || typeof val !== 'string' || !this.product) return ''
            return val.replaceAll(
                this.SUB.PRODUCT_NAME,
                this.product?.name || 'Offer'
            )
        },
        ctaProps(cta) {
            let props = {
                class: [],
            }

            if (cta.type === 'button-primary') {
                props.class.push('btn-primary')
            } else if (cta.type === 'button-secondary') {
                props.class.push('btn-secondary')
            } else if (cta.type === 'button-salmon') {
                props.class.push('btn-salmon')
            } else if (cta.type === 'link') {
                props.class.push(['ml-5 font-bold', 'underline'])
            }

            if (cta.url === this.SUB.GO_TO_CART) {
                props.href = this.abandonedCartUrl
            } else if (cta.url.indexOf('#') > -1 && window.location.search) {
                props.href =
                    cta.url.split('#')[0] +
                    window.location.search +
                    '#' +
                    cta.url.split('#')[1]
            } else {
                props.href = cta.url
            }
            if (cta.gaCategory || cta.gaAction || cta.gaLabel) {
                props.dataCategory = cta.gaCategory
                props.dataAction = cta.gaAction
                props.dataLabel = cta.gaLabel
            }

            return props
        },
        splitPrice(priceString) {
            return Number.parseFloat(priceString).toFixed(2).split('.')
        },
        showCountdownLayer(value) {
            this.showingCountdownLayer = value
        },
    },
}
</script>
